import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CloseIcon } from "@/lib/icons";

export interface SubtaskDraft {
  /** Stable identity for React keys — drafts have no server id yet. */
  key: string;
  title: string;
  description: string;
}

/**
 * One drafted subtask in the "Luo uusi tehtävä" dialog.
 *
 * Purely presentational and fully controlled: it holds no state and performs no
 * mutation. Drafts are collected in the dialog and only sent once the parent
 * task exists, so a row must be editable and removable right up to submit.
 *
 * Styled to match the inline new-subtask form in `TaskDialog.tsx` so a subtask
 * looks the same whether it is drafted here or added to an existing task later.
 *
 * Generic over the value so callers may carry extra fields alongside the three
 * this row edits and still get them back intact from `onChange`.
 */
export function SubtaskDraftRow<T extends SubtaskDraft>({
  value,
  index,
  onChange,
  onRemove,
  disabled = false,
  error,
  indexLabel,
}: {
  value: T;
  /** Zero-based; rendered as a 1-based ordinal. */
  index: number;
  onChange: (next: T) => void;
  onRemove: () => void;
  disabled?: boolean;
  /** Per-row failure message, set when this subtask's creation failed. */
  error?: string | null;
  /**
   * Ordinal shown before the title. Omit for the 1-based row position; pass
   * `null` to show none — template subtask titles already carry their own
   * numbering ("1. Johtoselvitys hankittu"), which the position would double up.
   */
  indexLabel?: string | null;
}) {
  return (
    <div
      className={`py-2 px-3 rounded-md border border-dashed bg-accent/20 space-y-2 min-w-0 ${
        error ? "border-red-400" : "border-primary/30"
      }`}
    >
      <div className="flex items-center gap-2 min-w-0">
        {indexLabel !== null && (
          <span className="text-xs text-muted-foreground shrink-0 tabular-nums">
            {indexLabel ?? `${index + 1}.`}
          </span>
        )}
        <Input
          value={value.title}
          onChange={(e) => onChange({ ...value, title: e.target.value })}
          placeholder="Alitehtävän nimi"
          className="text-base md:text-sm h-8 w-full max-w-full box-border min-w-0"
          disabled={disabled}
          aria-label={`Alitehtävän ${index + 1} nimi`}
        />
        <button
          type="button"
          onClick={onRemove}
          disabled={disabled}
          title="Poista alitehtävä"
          aria-label={`Poista alitehtävä ${index + 1}`}
          className="h-7 w-7 shrink-0 rounded-md flex items-center justify-center text-muted-foreground hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
        >
          <CloseIcon className="h-3.5 w-3.5" />
        </button>
      </div>
      <Textarea
        value={value.description}
        onChange={(e) => onChange({ ...value, description: e.target.value })}
        placeholder="Kuvaus (valinnainen)"
        className="text-base md:text-sm min-h-[50px] resize-none w-full max-w-full box-border"
        disabled={disabled}
        aria-label={`Alitehtävän ${index + 1} kuvaus`}
      />
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}
